import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, Check } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  secondary?: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'בחר...',
  className = '',
  compact = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  const selected = useMemo(
    () => options.find(o => o.value === value),
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      o => o.label.toLowerCase().includes(q) || (o.secondary?.toLowerCase().includes(q)),
    );
  }, [options, query]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        listRef.current && !listRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      updateDropdownPosition();
      inputRef.current?.focus();
      requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
      });
    }
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateDropdownPosition]);

  const handleSelect = useCallback((v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const py = compact ? 'py-1.5' : 'py-2';
  const textSize = compact ? 'text-xs' : 'text-sm';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger / Combobox input */}
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`w-full flex items-center gap-2 px-2.5 ${py} border border-gray-200
                     rounded-lg ${textSize} bg-white text-right
                     hover:border-blue-300 hover:shadow-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-400
                     transition-all cursor-pointer group`}
        >
          <span className={`flex-1 truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
            {selected ? selected.label : placeholder}
          </span>
          {value ? (
            <X
              className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 shrink-0"
              onClick={e => { e.stopPropagation(); onChange(''); }}
            />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500
                                    transition-colors shrink-0" />
          )}
        </button>
      ) : (
        <div className={`w-full flex items-center gap-1.5 px-2.5 ${py} border-2 border-blue-400
                        rounded-lg ${textSize} bg-white shadow-sm`}>
          <Search className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="חפש או בחר מהרשימה..."
            className={`flex-1 outline-none bg-transparent ${textSize} placeholder:text-gray-400`}
            onKeyDown={e => {
              if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              if (e.key === 'Enter' && filtered.length === 1 && filtered[0]) {
                handleSelect(filtered[0].value);
              }
            }}
          />
          {query ? (
            <X
              className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
              onClick={() => setQuery('')}
            />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          )}
        </div>
      )}

      {/* Dropdown — rendered in a portal so it is never clipped by overflow */}
      {open && createPortal(
        <div
          ref={listRef}
          style={dropdownStyle}
          className="z-[9999] bg-white border-2 border-blue-400
                     rounded-lg shadow-xl max-h-64 overflow-y-auto
                     scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          {/* Count header */}
          <div className="sticky top-0 z-10 px-3 py-1.5 bg-gray-50 border-b border-gray-100
                         text-[11px] text-gray-500 flex items-center justify-between">
            <span>
              {query
                ? `${filtered.length} מתוך ${options.length}`
                : `${options.length} פריטים`}
            </span>
            {!query && (
              <span className="text-gray-400">גלול או הקלד לסינון</span>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-gray-400">
              לא נמצאו תוצאות
            </div>
          ) : (
            filtered.map(opt => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  ref={isSelected ? selectedItemRef : undefined}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full text-right px-3 ${compact ? 'py-1.5' : 'py-2'} ${textSize}
                             transition-colors flex items-center gap-2 border-b border-gray-50
                             last:border-b-0
                             ${isSelected
                               ? 'bg-blue-50 text-blue-700 font-medium'
                               : 'text-gray-700 hover:bg-blue-50/60'}`}
                >
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  )}
                  <span className={`flex-1 truncate ${isSelected ? '' : 'mr-5'}`}>
                    {opt.label}
                  </span>
                  {opt.secondary && (
                    <span className="text-[10px] text-gray-400 font-mono shrink-0 dir-ltr" dir="ltr">
                      {opt.secondary}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
